import { useEffect, useState } from "react";
import SearchSelect from "../components/SearchSelect";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, Trash2, ShieldCheck, Link2 } from "lucide-react";

const STATUS_OPTIONS = ["DRAFT", "SENT", "RECEIVED", "CANCELLED"];
const CURRENCIES = ["USD", "EUR", "GBP", "INR", "CNY", "JPY", "AUD", "CAD"];

const Field = ({ label, children, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">{label}</label>
    <div className="mt-1" data-testid={testid}>{children}</div>
  </div>
);

const Input = (props) => (
  <input {...props} className={"w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 " + (props.className || "")} />
);

const TextArea = (props) => (
  <textarea {...props} className={"w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 " + (props.className || "")} />
);

const CellInput = (props) => (
  <input {...props} className={"w-full border border-transparent hover:border-zinc-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950 " + (props.className || "")} />
);

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [po, setPo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [generatingCustoms, setGeneratingCustoms] = useState(false);
  const [customsDocs, setCustomsDocs] = useState(null);

  useEffect(() => {
    // Supplier master for the governed picker below; empty is tolerated.
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => setSuppliers([]));
    api.get(`/purchase-orders/${id}`).then((r) => setPo(r.data)).catch(() => navigate("/purchase-orders"));
  }, [id, navigate]);

  if (!po) return <div className="p-10 text-sm text-zinc-400">Loading…</div>;
  const canEdit = user?.role === "admin" || user?.id === po.owner_id;
  const cur = po.currency || "USD";

  const set = (patch) => setPo({ ...po, ...patch });

  const totals = () => {
    let units = 0, total = 0;
    for (const it of po.items) {
      const q = Object.values(it.size_qty || {}).reduce((s, v) => s + (parseInt(v) || 0), 0);
      units += q;
      total += q * (parseFloat(it.unit_cost) || 0);
    }
    return { units, total };
  };
  const T = totals();

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/purchase-orders/${id}`, {
        status: po.status, supplier: po.supplier, buyer: po.buyer,
        currency: po.currency, issue_date: po.issue_date,
        expected_delivery_date: po.expected_delivery_date,
        payment_terms: po.payment_terms, delivery_terms: po.delivery_terms,
        items: po.items, notes: po.notes,
      });
      setPo(data);
      toast.success("Purchase order saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/purchase-orders/${id}/export/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po.po_number || "purchase-order"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF exported");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this purchase order?")) return;
    await api.delete(`/purchase-orders/${id}`);
    navigate("/purchase-orders");
  };

  const customsReady = po.items.length > 0 && po.items.every(
    (it) => (it.hs_code || "").trim() && (it.origin_declaration || "").trim()
  );

  const generateCustomsDocs = async () => {
    setGeneratingCustoms(true);
    try {
      const { data } = await api.post(`/purchase-orders/${id}/customs-docs`);
      setCustomsDocs(data);
      toast.success("Customs documentation generated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGeneratingCustoms(false);
    }
  };

  const updateItem = (i, patch) => {
    const items = [...po.items];
    items[i] = { ...items[i], ...patch };
    set({ items });
  };
  const updateSizeQty = (i, size, val) => {
    const items = [...po.items];
    items[i] = { ...items[i], size_qty: { ...items[i].size_qty, [size]: parseInt(val) || 0 } };
    set({ items });
  };
  const removeItem = (i) => set({ items: po.items.filter((_, x) => x !== i) });

  const statusStyle = {
    DRAFT: "bg-zinc-100 text-zinc-700",
    SENT: "bg-amber-100 text-amber-800",
    RECEIVED: "bg-emerald-100 text-emerald-800",
    CANCELLED: "bg-red-100 text-red-700",
  }[po.status] || "bg-zinc-100 text-zinc-700";

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <button data-testid="po-back" onClick={() => navigate("/purchase-orders")} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 transition-colors">
        <ArrowLeft size={14} /> Purchase Orders
      </button>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PO · {po.po_number}</p>
          <h1 className="font-heading text-3xl font-bold mt-1 tracking-tight" data-testid="po-title">{po.supplier?.name || "New Purchase Order"}</h1>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] font-medium px-2.5 py-1 rounded-full ${statusStyle}`}>{po.status}</span>
            <p className="text-sm text-zinc-500">Issued {po.issue_date} · By {po.owner_name}
              {po.collection_id && <> · <Link to={`/collections/${po.collection_id}`} className="underline hover:text-zinc-950">source collection</Link></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="po-save" onClick={save} disabled={!canEdit || saving}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 transition-colors">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
          <button data-testid="po-export" onClick={exportPdf} disabled={exporting}
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 disabled:opacity-40 transition-colors">
            <FileText size={14} /> {exporting ? "Exporting…" : "Export PDF"}
          </button>
          {canEdit && (
            <button data-testid="po-delete" onClick={del} className="p-2.5 border border-zinc-300 rounded-md hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4" data-testid="po-supplier">
          {/* ── Sourcing provenance ────────────────────────────────────────────────────────
              Which award this order honours. Absent on a PO raised standalone, which stays
              legitimate — the panel simply does not render. */}
          {po.rfq_id && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg px-4 py-3" data-testid="po-provenance">
              <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-medium flex items-center gap-1.5">
                <Link2 size={11} /> Sourcing chain
              </p>
              <p className="text-sm text-emerald-900 mt-1.5">
                From <Link to="/plm/rfqs" className="underline underline-offset-2 font-medium">RFQ {String(po.rfq_id).slice(0, 8)}</Link>
                {" → "}awarded quotation {String(po.quotation_id || "").slice(0, 8)}
                {po.supplier?.name ? <> {" → "}<Link to="/plm/suppliers" className="underline underline-offset-2 font-medium">{po.supplier.name}</Link></> : null}
              </p>
              <p className="text-xs text-emerald-800/70 mt-1">
                Price, currency, payment terms and quantity were carried from the awarded quotation.
              </p>
            </div>
          )}

          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Supplier</p>
          {/* Governed reference. On a PO raised from an award this is already set and locked to
              the awarded supplier; on a standalone PO it is chosen from the supplier master
              rather than typed, so the order names a supplier the system knows. */}
          <Field label="Supplier (master)">
            {po.supplier_id ? (
              <div className="flex items-center gap-2 border border-zinc-200 rounded-md px-3 py-2 text-sm bg-zinc-50">
                <span className="truncate">{po.supplier?.name || String(po.supplier_id).slice(0, 8)}</span>
                {po.rfq_id && <span className="ml-auto text-[10px] text-zinc-400">from award</span>}
              </div>
            ) : (
              <SearchSelect
                options={suppliers.map((sp) => ({
                  id: sp.id,
                  label: sp.company_name || sp.name || "Unnamed supplier",
                  sublabel: [sp.country, sp.status].filter(Boolean).join(" · "),
                  raw: sp,
                }))}
                value={null}
                onChange={(o) => o && set({
                  supplier_id: o.id,
                  supplier: { ...po.supplier, id: o.id, name: o.label, country: o.raw?.country || "" },
                })}
                placeholder="Search the supplier master…"
                emptyMessage="No suppliers in the master. Add suppliers in Suppliers first."
              />
            )}
          </Field>
          <Field label="Company name"><Input data-testid="supplier-name" value={po.supplier?.name || ""} onChange={(e) => set({ supplier: { ...po.supplier, name: e.target.value } })} /></Field>
          <Field label="Contact person"><Input value={po.supplier?.contact_person || ""} onChange={(e) => set({ supplier: { ...po.supplier, contact_person: e.target.value } })} /></Field>
          <Field label="Address"><TextArea rows={3} value={po.supplier?.address || ""} onChange={(e) => set({ supplier: { ...po.supplier, address: e.target.value } })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><Input type="email" value={po.supplier?.email || ""} onChange={(e) => set({ supplier: { ...po.supplier, email: e.target.value } })} /></Field>
            <Field label="Phone"><Input value={po.supplier?.phone || ""} onChange={(e) => set({ supplier: { ...po.supplier, phone: e.target.value } })} /></Field>
          </div>
        </div>

        <div className="space-y-4" data-testid="po-buyer">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Ship to / Buyer</p>
          <Field label="Company name"><Input value={po.buyer?.company_name || ""} onChange={(e) => set({ buyer: { ...po.buyer, company_name: e.target.value } })} /></Field>
          <Field label="Contact person"><Input value={po.buyer?.contact_person || ""} onChange={(e) => set({ buyer: { ...po.buyer, contact_person: e.target.value } })} /></Field>
          <Field label="Address"><TextArea rows={3} value={po.buyer?.address || ""} onChange={(e) => set({ buyer: { ...po.buyer, address: e.target.value } })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><Input value={po.buyer?.email || ""} onChange={(e) => set({ buyer: { ...po.buyer, email: e.target.value } })} /></Field>
            <Field label="Phone"><Input value={po.buyer?.phone || ""} onChange={(e) => set({ buyer: { ...po.buyer, phone: e.target.value } })} /></Field>
          </div>
        </div>

        <div className="space-y-4" data-testid="po-meta">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Terms & status</p>
          <Field label="Status">
            <select data-testid="po-status" value={po.status} onChange={(e) => set({ status: e.target.value })}
              className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Currency">
              <select data-testid="po-currency" value={po.currency} onChange={(e) => set({ currency: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Issue date"><Input type="date" value={po.issue_date} onChange={(e) => set({ issue_date: e.target.value })} /></Field>
          </div>
          <Field label="Expected delivery date"><Input type="date" value={po.expected_delivery_date} onChange={(e) => set({ expected_delivery_date: e.target.value })} /></Field>
          <Field label="Payment terms"><Input value={po.payment_terms} onChange={(e) => set({ payment_terms: e.target.value })} /></Field>
          <Field label="Delivery terms"><Input value={po.delivery_terms} onChange={(e) => set({ delivery_terms: e.target.value })} /></Field>
          <div className="border border-zinc-950 bg-zinc-950 text-white rounded-lg p-4 space-y-2" data-testid="po-totals-panel">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-zinc-400">Styles</p>
              <p className="text-sm">{po.items.length}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-zinc-400">Units</p>
              <p className="text-sm" data-testid="po-total-units">{T.units}</p>
            </div>
            <div className="border-t border-zinc-700 pt-2 flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider">Grand total</p>
              <p className="text-lg font-bold" data-testid="po-grand-total">{cur} {T.total.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8" data-testid="po-items">
        <h2 className="font-heading text-lg font-bold">Items</h2>
        {po.items.length === 0 ? (
          <div className="mt-4 border-2 border-dashed border-zinc-200 rounded-lg p-10 text-center text-zinc-400 text-sm">
            No styles on this PO. Delete and start again from a collection to add styles.
          </div>
        ) : (
          <div className="mt-3 space-y-4">
            {po.items.map((it, i) => {
              const lineQty = Object.values(it.size_qty || {}).reduce((s, v) => s + (parseInt(v) || 0), 0);
              const lineTotal = lineQty * (parseFloat(it.unit_cost) || 0);
              return (
                <div key={it.id} data-testid={`po-item-${i}`} className="border border-zinc-200 rounded-lg bg-white p-3">
                  <div className="flex items-start gap-4">
                    {it.cover_image && <img src={imgUrl(it.cover_image)} alt={it.style_name} className="w-24 h-32 object-cover rounded" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{it.style_number}</p>
                          <p className="text-sm font-medium truncate">{it.product_title || it.style_name}</p>
                          {it.product_title && it.product_title !== it.style_name && <p className="text-[11px] text-zinc-400 truncate">{it.style_name}</p>}
                          {it.product_description && (
                            <p className="text-[11px] text-zinc-500 mt-1 line-clamp-3 whitespace-pre-line" data-testid={`po-item-description-${i}`}>
                              {it.product_description}
                            </p>
                          )}
                          {it.techpack_id && (
                            <div className="flex items-center gap-2 mt-1 text-[11px] text-zinc-500">
                              <Link to={`/techpacks/${it.techpack_id}`} className="underline hover:text-zinc-950">techpack</Link>
                              {it.cost_sheet_id && <><span>·</span><Link to={`/costing/${it.cost_sheet_id}`} className="underline hover:text-zinc-950">cost sheet</Link></>}
                            </div>
                          )}
                        </div>
                        <button onClick={() => removeItem(i)} data-testid={`po-item-remove-${i}`} className="text-zinc-400 hover:text-red-600 text-xs">Remove</button>
                      </div>
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Field label="Colorway">
                          {(it.colorway_options || []).length > 0 ? (
                            <select data-testid={`po-item-colorway-${i}`} value={it.colorway_name || ""} onChange={(e) => updateItem(i, { colorway_name: e.target.value })}
                              className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                              <option value="">-</option>
                              {(it.colorway_options || []).map((n) => <option key={n}>{n}</option>)}
                            </select>
                          ) : (
                            <Input value={it.colorway_name || ""} onChange={(e) => updateItem(i, { colorway_name: e.target.value })} />
                          )}
                        </Field>
                        <Field label={`Unit cost (${cur})`}>
                          <Input data-testid={`po-item-unit-cost-${i}`} type="number" step="0.01" value={it.unit_cost} onChange={(e) => updateItem(i, { unit_cost: parseFloat(e.target.value) || 0 })} />
                        </Field>
                        <div className="col-span-2 md:col-span-2 flex items-end justify-end gap-6 text-xs">
                          <div className="text-right">
                            <p className="text-[10px] uppercase text-zinc-400">Line qty</p>
                            <p className="text-sm font-medium">{lineQty}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] uppercase text-zinc-400">Line total</p>
                            <p className="text-sm font-medium" data-testid={`po-item-line-total-${i}`}>{cur} {lineTotal.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Field label="HS code">
                          <Input data-testid={`po-item-hs-code-${i}`} placeholder="e.g. 6204.43" value={it.hs_code || ""} onChange={(e) => updateItem(i, { hs_code: e.target.value })} />
                        </Field>
                        <Field label="Origin declaration">
                          <Input data-testid={`po-item-origin-${i}`} placeholder="e.g. Made in Portugal" value={it.origin_declaration || ""} onChange={(e) => updateItem(i, { origin_declaration: e.target.value })} />
                        </Field>
                      </div>
                      <div className="mt-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Size breakdown</p>
                        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${(it.sizes || []).length}, minmax(0,1fr))` }}>
                          {(it.sizes || []).map((s) => (
                            <div key={s} className="border border-zinc-200 rounded overflow-hidden">
                              <p className="text-[10px] text-center py-1 bg-zinc-100 font-medium">{s}</p>
                              <CellInput type="number" min="0" data-testid={`po-item-${i}-size-${s}`} className="text-center"
                                value={it.size_qty?.[s] ?? 0} onChange={(e) => updateSizeQty(i, s, e.target.value)} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3" data-testid="po-customs-actions">
        <button data-testid="po-generate-customs" onClick={generateCustomsDocs} disabled={!customsReady || generatingCustoms}
          className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 disabled:opacity-40 transition-colors"
          title={!customsReady ? "All items must have HS code and origin declaration" : "Generate customs documentation"}>
          <ShieldCheck size={14} /> {generatingCustoms ? "Generating…" : "Generate Customs Docs"}
        </button>
        {!customsReady && po.items.length > 0 && (
          <p className="text-xs text-amber-600">Fill in HS code and origin declaration for all items to enable customs docs.</p>
        )}
      </div>

      {customsDocs && (
        <div className="mt-6 space-y-6" data-testid="po-customs-docs">
          <h2 className="font-heading text-lg font-bold">Customs Documentation</h2>
          <p className="text-xs text-zinc-400">Generated {new Date(customsDocs.generated_at).toLocaleString()}</p>

          <div className="border border-zinc-200 rounded-lg bg-white p-4" data-testid="customs-commercial-invoice">
            <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700 mb-3">Commercial Invoice — {customsDocs.commercial_invoice.invoice_number}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-zinc-600 mb-3">
              <div><span className="text-zinc-400">Date:</span> {customsDocs.commercial_invoice.date}</div>
              <div><span className="text-zinc-400">Currency:</span> {customsDocs.commercial_invoice.currency}</div>
              <div><span className="text-zinc-400">Terms:</span> {customsDocs.commercial_invoice.delivery_terms}</div>
              <div><span className="text-zinc-400">Total:</span> {customsDocs.commercial_invoice.currency} {customsDocs.commercial_invoice.total_value?.toFixed(2)}</div>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-wider text-zinc-400">
                <th className="py-1.5 pr-2">Style</th><th className="py-1.5 pr-2">Description</th><th className="py-1.5 pr-2">HS Code</th><th className="py-1.5 pr-2">Origin</th>
                <th className="py-1.5 pr-2 text-right">Qty</th><th className="py-1.5 pr-2 text-right">Unit price</th><th className="py-1.5 text-right">Total</th>
              </tr></thead>
              <tbody>
                {customsDocs.commercial_invoice.lines.map((ln, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="py-1.5 pr-2">{ln.style_number}</td><td className="py-1.5 pr-2">{ln.description}</td><td className="py-1.5 pr-2 font-mono">{ln.hs_code}</td>
                    <td className="py-1.5 pr-2">{ln.origin}</td><td className="py-1.5 pr-2 text-right">{ln.quantity}</td>
                    <td className="py-1.5 pr-2 text-right">{ln.unit_price?.toFixed(2)}</td><td className="py-1.5 text-right">{ln.line_total?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-zinc-200 rounded-lg bg-white p-4" data-testid="customs-packing-list">
            <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700 mb-3">Packing List — {customsDocs.packing_list.reference}</h3>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-wider text-zinc-400">
                <th className="py-1.5 pr-2">Style</th><th className="py-1.5 pr-2">Description</th><th className="py-1.5 pr-2">Colorway</th><th className="py-1.5 pr-2">Size breakdown</th><th className="py-1.5 text-right">Units</th>
              </tr></thead>
              <tbody>
                {customsDocs.packing_list.lines.map((ln, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="py-1.5 pr-2">{ln.style_number}</td><td className="py-1.5 pr-2">{ln.description}</td><td className="py-1.5 pr-2">{ln.colorway}</td>
                    <td className="py-1.5 pr-2 font-mono">{Object.entries(ln.size_breakdown || {}).map(([s, q]) => `${s}:${q}`).join(" ")}</td>
                    <td className="py-1.5 text-right">{ln.total_units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-zinc-200 rounded-lg bg-white p-4" data-testid="customs-certificate-of-origin">
            <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700 mb-3">Certificate of Origin — {customsDocs.certificate_of_origin.reference}</h3>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-wider text-zinc-400">
                <th className="py-1.5 pr-2">Style</th><th className="py-1.5 pr-2">Description</th><th className="py-1.5 pr-2">HS Code</th><th className="py-1.5">Origin declaration</th>
              </tr></thead>
              <tbody>
                {customsDocs.certificate_of_origin.entries.map((en, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="py-1.5 pr-2">{en.style_number}</td><td className="py-1.5 pr-2">{en.description}</td>
                    <td className="py-1.5 pr-2 font-mono">{en.hs_code}</td><td className="py-1.5">{en.origin_declaration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8">
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Notes</label>
        <TextArea data-testid="po-notes" rows={3} value={po.notes || ""} onChange={(e) => set({ notes: e.target.value })}
          placeholder="Special instructions to the supplier, packaging or delivery notes..." className="mt-1" />
      </div>
    </div>
  );
}
